import { DEFAULT_REVIEW_SHORTCUTS, type ReviewShortcuts } from "@engo/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button.js";
import { Card } from "../../components/ui/card.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import { Modal } from "../../components/ui/Modal.js";
import { Textarea } from "../../components/ui/textarea.js";
import { ApiError, api } from "../../lib/api.js";

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      {children}
      {hint ? <p className="mt-1 text-xs text-[var(--text-muted)]">{hint}</p> : null}
    </label>
  );
}

function displayKey(key: string): string {
  if (key === " ") return "Space";
  return key;
}

function ShortcutCapture({
  label,
  value,
  onChange,
  listeningLabel,
}: {
  label: string;
  value: string;
  onChange: (key: string) => void;
  listeningLabel: string;
}) {
  const [listening, setListening] = useState(false);

  useEffect(() => {
    if (!listening) return;
    function handleKeyDown(e: KeyboardEvent) {
      e.preventDefault();
      if (e.key !== "Escape") onChange(e.key);
      setListening(false);
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [listening, onChange]);

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-[var(--text-secondary)]">{label}</span>
      <button
        type="button"
        onClick={() => setListening(true)}
        className={`min-w-[96px] rounded-lg border px-3 py-1.5 text-center font-mono text-sm transition-colors ${
          listening
            ? "border-[var(--primary)] text-[var(--primary)]"
            : "border-[var(--glass-border)] text-[var(--text-primary)] hover:bg-white/5"
        }`}
      >
        {listening ? listeningLabel : displayKey(value)}
      </button>
    </div>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-[var(--text-secondary)]">{label}</span>
      <span className="font-mono text-sm text-[var(--text-primary)]">{value}</span>
    </div>
  );
}

function SettingsCardHeader({
  title,
  onEdit,
  editable,
}: {
  title: string;
  onEdit: () => void;
  editable: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between">
      <h2 className="font-display text-lg font-semibold text-[var(--text-primary)]">{title}</h2>
      {editable ? (
        <button
          type="button"
          onClick={onEdit}
          aria-label={t("settings.editButton")}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-muted)] transition-colors hover:bg-white/10 hover:text-[var(--text-primary)]"
        >
          <Pencil className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

function ModalNotice({ notice, noticeTone }: { notice: string | null; noticeTone: "neutral" | "error" }) {
  if (!notice) return null;
  return (
    <p className={`text-xs ${noticeTone === "error" ? "text-rose-300" : "text-[var(--text-muted)]"}`}>
      {notice}
    </p>
  );
}

function ModalFooterActions({
  isSaving,
  onSave,
  onCancel,
}: {
  isSaving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Button onClick={onCancel} disabled={isSaving} variant="secondary">
        {t("settings.cancel")}
      </Button>
      <Button onClick={onSave} disabled={isSaving} variant="primary">
        {isSaving ? t("settings.saving") : t("settings.save")}
      </Button>
    </>
  );
}

interface ReviewEditModalProps {
  dailyNewCardLimit: number;
  setDailyNewCardLimit: (v: number) => void;
  desiredRetentionPct: number;
  setDesiredRetentionPct: (v: number) => void;
  reviewShortcuts: ReviewShortcuts;
  setShortcut: (action: keyof ReviewShortcuts) => (key: string) => void;
  isSaving: boolean;
  notice: string | null;
  noticeTone: "neutral" | "error";
  onSave: () => void;
  onCancel: () => void;
}

function ReviewEditModal({
  dailyNewCardLimit,
  setDailyNewCardLimit,
  desiredRetentionPct,
  setDesiredRetentionPct,
  reviewShortcuts,
  setShortcut,
  isSaving,
  notice,
  noticeTone,
  onSave,
  onCancel,
}: ReviewEditModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      title={t("settings.reviewCardTitle")}
      onClose={onCancel}
      footer={<ModalFooterActions isSaving={isSaving} onSave={onSave} onCancel={onCancel} />}
    >
      <Field label={t("settings.fieldDailyNewCardLimit")} hint={t("settings.hintDailyNewCardLimit")}>
        <Input
          type="number"
          min={0}
          max={200}
          value={dailyNewCardLimit}
          onChange={(e) => setDailyNewCardLimit(Number(e.target.value))}
        />
      </Field>
      <Field label={t("settings.fieldDesiredRetention")} hint={t("settings.hintDesiredRetention")}>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={70}
            max={97}
            value={desiredRetentionPct}
            onChange={(e) => setDesiredRetentionPct(Number(e.target.value))}
            className="w-full accent-[var(--primary)]"
          />
          <span className="w-12 shrink-0 text-right text-sm text-[var(--text-primary)]">
            {desiredRetentionPct}%
          </span>
        </div>
      </Field>

      <div className="border-t border-[var(--glass-border)] pt-4">
        <p className="text-sm font-medium text-[var(--text-primary)]">{t("settings.shortcutsTitle")}</p>
        <p className="mt-1 text-xs text-[var(--text-muted)]">{t("settings.shortcutsHint")}</p>
        <div className="mt-3 space-y-2">
          <ShortcutCapture
            label={t("settings.shortcutReveal")}
            value={reviewShortcuts.reveal}
            onChange={setShortcut("reveal")}
            listeningLabel={t("settings.shortcutListening")}
          />
          <ShortcutCapture
            label={t("settings.shortcutHint")}
            value={reviewShortcuts.hint}
            onChange={setShortcut("hint")}
            listeningLabel={t("settings.shortcutListening")}
          />
          <ShortcutCapture
            label={t("settings.shortcutAgain")}
            value={reviewShortcuts.again}
            onChange={setShortcut("again")}
            listeningLabel={t("settings.shortcutListening")}
          />
          <ShortcutCapture
            label={t("settings.shortcutHard")}
            value={reviewShortcuts.hard}
            onChange={setShortcut("hard")}
            listeningLabel={t("settings.shortcutListening")}
          />
          <ShortcutCapture
            label={t("settings.shortcutGood")}
            value={reviewShortcuts.good}
            onChange={setShortcut("good")}
            listeningLabel={t("settings.shortcutListening")}
          />
          <ShortcutCapture
            label={t("settings.shortcutEasy")}
            value={reviewShortcuts.easy}
            onChange={setShortcut("easy")}
            listeningLabel={t("settings.shortcutListening")}
          />
        </div>
      </div>

      <ModalNotice notice={notice} noticeTone={noticeTone} />
    </Modal>
  );
}

function PersonalizationEditModal({
  personalContext,
  setPersonalContext,
  isSaving,
  notice,
  noticeTone,
  onSave,
  onCancel,
}: {
  personalContext: string;
  setPersonalContext: (v: string) => void;
  isSaving: boolean;
  notice: string | null;
  noticeTone: "neutral" | "error";
  onSave: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Modal
      title={t("settings.personalizationCardTitle")}
      onClose={onCancel}
      footer={<ModalFooterActions isSaving={isSaving} onSave={onSave} onCancel={onCancel} />}
    >
      <Field
        label={t("settings.fieldPersonalContext")}
        hint={t("settings.hintPersonalContext")}
      >
        <Textarea
          rows={3}
          maxLength={300}
          value={personalContext}
          onChange={(e) => setPersonalContext(e.target.value)}
          placeholder={t("settings.placeholderPersonalContext")}
        />
      </Field>

      <ModalNotice notice={notice} noticeTone={noticeTone} />
    </Modal>
  );
}

export function SettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });

  const [isEditingReview, setIsEditingReview] = useState(false);
  const [isEditingPersonalization, setIsEditingPersonalization] = useState(false);
  const [dailyNewCardLimit, setDailyNewCardLimit] = useState(10);
  const [desiredRetentionPct, setDesiredRetentionPct] = useState(90);
  const [reviewShortcuts, setReviewShortcuts] = useState<ReviewShortcuts>(DEFAULT_REVIEW_SHORTCUTS);
  const [personalContext, setPersonalContext] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeTone, setNoticeTone] = useState<"neutral" | "error">("neutral");

  function loadFromSettings() {
    if (query.status !== "success") return;
    setDailyNewCardLimit(query.data.daily_new_card_limit);
    setDesiredRetentionPct(Math.round(query.data.desired_retention * 100));
    setReviewShortcuts(query.data.shortcuts.review);
    setPersonalContext(query.data.personal_context);
  }

  useEffect(loadFromSettings, [query.status, query.data]);

  function setShortcut(action: keyof ReviewShortcuts) {
    return (key: string) => setReviewShortcuts((prev) => ({ ...prev, [action]: key }));
  }

  function openEditReview() {
    setNotice(null);
    setIsEditingReview(true);
  }

  function cancelEditReview() {
    loadFromSettings();
    setNotice(null);
    setIsEditingReview(false);
  }

  function openEditPersonalization() {
    setNotice(null);
    setIsEditingPersonalization(true);
  }

  function cancelEditPersonalization() {
    loadFromSettings();
    setNotice(null);
    setIsEditingPersonalization(false);
  }

  async function saveReview() {
    setIsSaving(true);
    setNotice(null);
    try {
      const updated = await api.updateSettings({
        daily_new_card_limit: dailyNewCardLimit,
        desired_retention: desiredRetentionPct / 100,
        shortcuts: { review: reviewShortcuts },
      });
      queryClient.setQueryData(["settings"], updated);
      setIsEditingReview(false);
      setNoticeTone("neutral");
      setNotice(t("settings.noticeSaved"));
    } catch (err) {
      setNoticeTone("error");
      setNotice(err instanceof ApiError ? err.message : t("settings.errorSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  }

  async function savePersonalization() {
    setIsSaving(true);
    setNotice(null);
    try {
      const updated = await api.updateSettings({ personal_context: personalContext });
      queryClient.setQueryData(["settings"], updated);
      setIsEditingPersonalization(false);
      setNoticeTone("neutral");
      setNotice(t("settings.noticeSaved"));
    } catch (err) {
      setNoticeTone("error");
      setNotice(err instanceof ApiError ? err.message : t("settings.errorSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
          {t("settings.title")}
        </h1>
        <p className="mt-1 text-[var(--text-secondary)]">{t("settings.subtitle")}</p>
      </div>

      <Card className="max-w-xl space-y-4">
        <SettingsCardHeader
          title={t("settings.reviewCardTitle")}
          onEdit={openEditReview}
          editable={query.status === "success"}
        />

        {query.status === "pending" ? (
          <p className="text-[var(--text-secondary)]">{t("settings.loading")}</p>
        ) : query.status === "error" ? (
          <p className="text-sm text-rose-300">
            {query.error instanceof ApiError ? query.error.message : t("settings.errorLoad")}
          </p>
        ) : (
          <>
            <ReadOnlyRow
              label={t("settings.fieldDailyNewCardLimit")}
              value={String(dailyNewCardLimit)}
            />
            <ReadOnlyRow
              label={t("settings.fieldDesiredRetention")}
              value={`${desiredRetentionPct}%`}
            />
            <div className="border-t border-[var(--glass-border)] pt-4">
              <p className="text-sm font-medium text-[var(--text-primary)]">
                {t("settings.shortcutsTitle")}
              </p>
              <div className="mt-3 space-y-2">
                <ReadOnlyRow
                  label={t("settings.shortcutReveal")}
                  value={displayKey(reviewShortcuts.reveal)}
                />
                <ReadOnlyRow label={t("settings.shortcutHint")} value={displayKey(reviewShortcuts.hint)} />
                <ReadOnlyRow
                  label={t("settings.shortcutAgain")}
                  value={displayKey(reviewShortcuts.again)}
                />
                <ReadOnlyRow label={t("settings.shortcutHard")} value={displayKey(reviewShortcuts.hard)} />
                <ReadOnlyRow label={t("settings.shortcutGood")} value={displayKey(reviewShortcuts.good)} />
                <ReadOnlyRow label={t("settings.shortcutEasy")} value={displayKey(reviewShortcuts.easy)} />
              </div>
            </div>
          </>
        )}
      </Card>

      <Card className="max-w-xl space-y-4">
        <SettingsCardHeader
          title={t("settings.personalizationCardTitle")}
          onEdit={openEditPersonalization}
          editable={query.status === "success"}
        />

        {query.status === "success" ? (
          <div>
            <p className="text-sm text-[var(--text-secondary)]">{t("settings.fieldPersonalContext")}</p>
            <p className="mt-1 text-sm text-[var(--text-primary)]">
              {personalContext || t("settings.personalContextEmpty")}
            </p>
          </div>
        ) : null}

        {!isEditingReview && !isEditingPersonalization && notice ? (
          <p className={`text-xs ${noticeTone === "error" ? "text-rose-300" : "text-[var(--text-muted)]"}`}>
            {notice}
          </p>
        ) : null}
      </Card>

      {isEditingReview ? (
        <ReviewEditModal
          dailyNewCardLimit={dailyNewCardLimit}
          setDailyNewCardLimit={setDailyNewCardLimit}
          desiredRetentionPct={desiredRetentionPct}
          setDesiredRetentionPct={setDesiredRetentionPct}
          reviewShortcuts={reviewShortcuts}
          setShortcut={setShortcut}
          isSaving={isSaving}
          notice={notice}
          noticeTone={noticeTone}
          onSave={() => void saveReview()}
          onCancel={cancelEditReview}
        />
      ) : null}

      {isEditingPersonalization ? (
        <PersonalizationEditModal
          personalContext={personalContext}
          setPersonalContext={setPersonalContext}
          isSaving={isSaving}
          notice={notice}
          noticeTone={noticeTone}
          onSave={() => void savePersonalization()}
          onCancel={cancelEditPersonalization}
        />
      ) : null}
    </div>
  );
}
